import { simpleLerp } from '../../../lib/math';
import type { TimeData } from '../../core/TimeData';
import { PortCallEventType } from '../../core/TimeData';
import {
  isCargoEventTimeData,
  isDischargeEvent,
  isLoadEvent,
  isPortCallArrivalEvent,
  isPortCallBerthingEventTimeData,
  isPortCallDepartureEvent,
  isPortCallEventTimeData,
} from '../../core/TimeData';
import { findLast, memoize } from 'lodash';
import type { ScenarioTimeline_ShipFragment } from '../../../graphql';
import { memoizeN } from '@seaber/common/lib/memoize';

import { stringToNumOpt } from '../../core/util';
import { isDefined } from '../../../lib/util';

type DraftDatapoint = [cargoMass: number, draft: number];
enum DatapointDimension {
  MASS = 0,
  DRAFT = 1,
}

/**
 * When cargo load on the vessel is more than
 * maxDraftAtLocation * OVERDRAFT_THRESHOLD we display
 * a yellow warning;
 * When it is more than maxDraftAtLocation we display
 * a red warning
 */
const OVERDRAFT_THRESHOLD = 0.95;

/**
 *  The severity of vessel overdraft
 *  'warning' if (max-draft-at-port * OVERDRAFT_THRESHOLD < vessel-draft < max-draft-at-port)
 *  'error' if (max-draft-at-port < vessel-draft)
 */
export type OverDraftWarningSeverity = 'warning' | 'error';

/**
 *  Extracts vessel draft data points for estimation of an actual draft
 *  of the vessel at a given cargo load.
 *  @param nodes
 *  @returns an array of draft datapoints, where each datapoint
 *  is a tuple of `[cargoLoad, draftAtLoad]:[number, number]`
 */
export const getVesselDraftDatapoints = memoize(
  (vessel: ScenarioTimeline_ShipFragment): Array<DraftDatapoint> => {
    if (!vessel.vesselDraftDatapointsByShipId.nodes.length) return [];

    return vessel.vesselDraftDatapointsByShipId.nodes.reduce((acc, data) => {
      if (!data) return acc;

      const { cargoLoad, draft } = data;

      if (!cargoLoad || !draft) return acc;

      acc.push([Number(cargoLoad), Number(draft)]);
      return acc;
    }, [] as Array<DraftDatapoint>);
  },
);

/**
 * Generic function to find line segment containing a target value.
 * @param datapoints - Array of [x, y] tuples sorted by x
 * @param targetValue - Value to search for
 * @param dimension - 0 for x-axis (cargo load), 1 for y-axis (draft)
 */
const getDatapointSegment = function <T extends DraftDatapoint>(
  datapoints: Array<T>,
  targetValue: number,
  dimension: DatapointDimension,
): [T, T] | undefined {
  const sortedDatapoints = datapoints.sort((a, b) => a[0] - b[0]);
  if (sortedDatapoints.length <= 1) return undefined;

  if (targetValue <= sortedDatapoints[0][dimension]) {
    return sortedDatapoints.slice(0, 2) as [T, T];
  }
  if (targetValue >= sortedDatapoints[sortedDatapoints.length - 1][dimension]) {
    return sortedDatapoints.slice(
      sortedDatapoints.length - 2,
      sortedDatapoints.length,
    ) as [T, T];
  }

  for (let i = 0; i < sortedDatapoints.length - 1; i++) {
    const currentPoint = sortedDatapoints[i];
    const nextPoint = sortedDatapoints[i + 1];

    if (
      targetValue >= currentPoint[dimension] &&
      targetValue < nextPoint[dimension]
    ) {
      return [currentPoint, nextPoint];
    }
  }
  return undefined;
};

/**
 * Calculates the line segment containing specified cargoLoad from a set
 * of vessel datapoints.
 *
 * E.g. given a set of `[[1, 3], [3, 6], [7, 10]]` and `cargoLoad` of `4`
 * it will return line segment `[[3, 6], [7, 10]]`
 */
export const getDraftDatapointSegmentFromLoad = function <
  T extends DraftDatapoint,
>({
  draftDatapoints,
  cargoLoad,
}: {
  draftDatapoints: Array<T>;
  cargoLoad: number;
}): [T, T] | undefined {
  return getDatapointSegment(
    draftDatapoints,
    cargoLoad,
    DatapointDimension.MASS,
  );
};

/**
 * Calculates the line segment containing specified draft from a set
 * of vessel datapoints. Inverse of getDraftDatapointSegmentFromLoad.
 *
 * E.g. given a set of `[[1000, 5], [2000, 8], [3000, 12]]` and `draft` of `9`
 * it will return line segment `[[2000, 8], [3000, 12]]`
 */
export const getDraftDatapointSegmentFromDraft = function <
  T extends DraftDatapoint,
>({
  draftDatapoints,
  draft,
}: {
  draftDatapoints: Array<T>;
  draft: number;
}): [T, T] | undefined {
  return getDatapointSegment(draftDatapoints, draft, DatapointDimension.DRAFT);
};

/**
 * Calculates maximum cargo load a vessel can carry given a draft constraint.
 * Inverse of estimateActualVesselDraft
 *
 * @param draftDatapoints - Array of [cargoLoad, draft] tuples
 * @param targetDraft - Maximum allowed draft at port (meters)
 * @returns Maximum cargo load (tons), or undefined if cannot determine. 0 if no capacity satisfies the draft.
 */
export const estimateCargoLoadFromDraft = (
  draftDatapoints: Array<DraftDatapoint>,
  targetDraft: number,
): number | undefined => {
  if (draftDatapoints.length === 0) return undefined;

  const sortedPoints = [...draftDatapoints].sort((a, b) => a[0] - b[0]);

  if (sortedPoints[0][DatapointDimension.DRAFT] > targetDraft) {
    return 0;
  }

  if (
    sortedPoints[sortedPoints.length - 1][DatapointDimension.DRAFT] <=
    targetDraft
  ) {
    return sortedPoints[sortedPoints.length - 1][DatapointDimension.MASS];
  }

  const segment = getDraftDatapointSegmentFromDraft({
    draftDatapoints: sortedPoints,
    draft: targetDraft,
  });

  if (!segment) return undefined;

  try {
    const [load1, draft1] = segment[0];
    const [load2, draft2] = segment[1];
    const segmentAsDraftToLoad: [DraftDatapoint, DraftDatapoint] = [
      [draft1, load1],
      [draft2, load2],
    ];
    return simpleLerp(targetDraft, segmentAsDraftToLoad);
  } catch (_) {
    return undefined;
  }
};

/**
 * Calculates the maximum cargo load a vessel can carry given a draft constraint.
 * If the vessel has no draft datapoints, returns 0 if the vessel's draft is more than the target draft, otherwise undefined.
 */
export const getVesselCargoLoadFromDraft = memoizeN(
  (vessel: ScenarioTimeline_ShipFragment, draft: number) => {
    const draftDatapoints = getVesselDraftDatapoints(vessel);
    if (draftDatapoints.length === 0) {
      return vessel.draft && +vessel.draft > draft ? 0 : undefined;
    }
    return estimateCargoLoadFromDraft(draftDatapoints, draft);
  },
);

/**
 * Maximum total on-board mass whose draft stays within a port's max draft — the exact inverse of
 * `estimateActualVesselDraft`'s forward curve, extrapolation included. Tri-state so callers separate a
 * real quantity cap from an unusable route:
 *  - `0`        even the empty vessel is too deep for the port (the first segment extrapolated down to
 *               mass 0 already exceeds the port draft); the port cannot be entered at any load. Never
 *               used as a mass cap — this is the invalid-routing flag only.
 *  - `Infinity` the port never constrains this vessel: no draft data, a single usable datapoint within
 *               the port draft, a flat or inverted top slope, or a curve that could not be evaluated.
 *               Fails open so a missing cap can never zero out a cargo.
 *  - finite > 0 the heaviest on-board mass the vessel can carry into the port within its draft. When the
 *               port is deeper than the heaviest datapoint the cap extrapolates the last segment's slope
 *               up to a finite mass — the safety-critical case, since a flat cap there would under-read
 *               draft and permit overloading the port.
 *
 * Shares `getDraftDatapointSegmentFromDraft` + `simpleLerp` with `estimateCargoLoadFromDraft` for the
 * interior interpolation, and continues the same end-segment slopes past both endpoints.
 */
export const getPortDraftMassCap = memoizeN(
  (vessel: ScenarioTimeline_ShipFragment, portMaxDraft: number): number => {
    const draftDatapoints = getVesselDraftDatapoints(vessel);
    if (draftDatapoints.length === 0) {
      const vesselDraft = vessel.draft ? Number(vessel.draft) : undefined;
      if (vesselDraft === undefined) return Infinity;
      return vesselDraft > portMaxDraft ? 0 : Infinity;
    }

    const sortedPoints = [...draftDatapoints].sort((a, b) => a[0] - b[0]);

    // A single usable datapoint gives no slope to extrapolate along; treat it like a single-value
    // vessel — too deep flags invalid routing, otherwise unconstrained.
    if (sortedPoints.length < 2) {
      return sortedPoints[0][DatapointDimension.DRAFT] > portMaxDraft
        ? 0
        : Infinity;
    }

    const firstMass = sortedPoints[0][DatapointDimension.MASS];
    const firstDraft = sortedPoints[0][DatapointDimension.DRAFT];
    const lastMass =
      sortedPoints[sortedPoints.length - 1][DatapointDimension.MASS];
    const lastDraft =
      sortedPoints[sortedPoints.length - 1][DatapointDimension.DRAFT];

    const slopeFirst =
      (sortedPoints[1][DatapointDimension.DRAFT] - firstDraft) /
      (sortedPoints[1][DatapointDimension.MASS] - firstMass);
    const slopeLast =
      (lastDraft -
        sortedPoints[sortedPoints.length - 2][DatapointDimension.DRAFT]) /
      (lastMass -
        sortedPoints[sortedPoints.length - 2][DatapointDimension.MASS]);

    // A flat or inverted top slope cannot be inverted to a finite mass; fail open.
    if (!Number.isFinite(slopeLast) || slopeLast <= 0) return Infinity;

    // Extrapolate the first segment down to an empty vessel. A port shallower than that draft cannot be
    // entered at any load ⇒ invalid routing.
    const zeroLoadDraft = firstDraft - firstMass * slopeFirst;
    if (portMaxDraft < zeroLoadDraft) return 0;

    // Port deeper than the whole loaded curve: extrapolate the last segment up to the finite mass whose
    // draft reaches the port limit.
    if (portMaxDraft >= lastDraft) {
      const cap = lastMass + (portMaxDraft - lastDraft) / slopeLast;
      return Number.isNaN(cap) ? Infinity : cap;
    }

    // Port shallower than the lightest laden datapoint but still enterable empty: a small mass on the
    // extrapolated-down first segment.
    if (portMaxDraft <= firstDraft) {
      const cap = firstMass + (portMaxDraft - firstDraft) / slopeFirst;
      return Number.isNaN(cap) ? Infinity : cap;
    }

    const segment = getDraftDatapointSegmentFromDraft({
      draftDatapoints: sortedPoints,
      draft: portMaxDraft,
    });
    if (!segment) return Infinity;

    try {
      const [load1, draft1] = segment[0];
      const [load2, draft2] = segment[1];
      const segmentAsDraftToLoad: [DraftDatapoint, DraftDatapoint] = [
        [draft1, load1],
        [draft2, load2],
      ];
      const cap = simpleLerp(portMaxDraft, segmentAsDraftToLoad);
      return Number.isNaN(cap) ? Infinity : cap;
    } catch (_) {
      return Infinity;
    }
  },
);

/**
 * The vessel's shallowest laden draft — the draft at its lowest-mass datapoint, or the single-value
 * draft when there are no datapoints. Shown as the estimated draft on an invalid-routing warning, where
 * `getPortDraftMassCap` returns 0 because even the empty vessel is too deep for the port.
 */
const getMinimumVesselDraft = (
  vessel: ScenarioTimeline_ShipFragment,
): number | undefined => {
  const datapoints = getVesselDraftDatapoints(vessel);
  if (datapoints.length === 0) {
    return vessel.draft ? Number(vessel.draft) : undefined;
  }
  return [...datapoints].sort((a, b) => a[0] - b[0])[0][
    DatapointDimension.DRAFT
  ];
};

type DraftEstimationData = { usedMassCapacity: number; maxPortDraft: number };
const UNDEFINED_DRAFT_ESTIMATION_DATA: Partial<DraftEstimationData> = {
  usedMassCapacity: undefined,
  maxPortDraft: undefined,
};
/**
 *  Checks incoming timedata to make sure it represents either
 *  departure or arrival port call event,
 *  extracts data used to estimate actual draft of the vessel at the event
 */
const getDraftEstimationDataFromTimeData = memoize(function (
  td: TimeData | undefined,
): Partial<DraftEstimationData> {
  if (
    !td ||
    !td.event ||
    (!isPortCallArrivalEvent(td.event) &&
      !isPortCallDepartureEvent(td.event)) ||
    !td.event.location ||
    !td.event.location.draft
  )
    return UNDEFINED_DRAFT_ESTIMATION_DATA;
  const { usedMassCapacity } = td;

  // Skip if there is no cargo load
  if (usedMassCapacity === 0) return UNDEFINED_DRAFT_ESTIMATION_DATA;
  const maxPortDraft = Number(td.event.location.draft);
  return { usedMassCapacity, maxPortDraft };
});

export type EventDraftIssue = {
  draftIssueSeverity: OverDraftWarningSeverity;
  estimatedVesselDraft: number;
  portMaxDraft: number;
};

/**
 * Calculates the severity of vessel overdraft at a port based on params.
 *
 * @param coefficients for linear regression interpolation
 * @param portMaxDraft max allowed draft at a given port
 * @param usedMassCapacity used mass capacity of the vessel
 * @returns `EventDraftIssues` object, or undefined if no overdraft was detected
 * The resulting object contains data on the estimated vessel draft and max allowed
 * draft at location for ease of display. The severity of the warning works as follows:
 *  - 'warning' if vessel draft almost exceeds max draft at port, according to `OVERDRAFT_THRESHOLD`;
 *  - 'error' if vessel draft exceeds max draft at port
 *  - `undefined` if no overdraft was detected
 */
export const getPortDraftWarningInfo = function (
  estimatedVesselDraft: number,
  portMaxDraft: number,
): EventDraftIssue | undefined {
  let draftIssueSeverity: OverDraftWarningSeverity | undefined = undefined;
  if (estimatedVesselDraft - Number(portMaxDraft) * OVERDRAFT_THRESHOLD > 0) {
    draftIssueSeverity = 'warning';
  }
  if (estimatedVesselDraft > Number(portMaxDraft)) {
    draftIssueSeverity = 'error';
  }
  if (!draftIssueSeverity) return undefined;
  return {
    draftIssueSeverity,
    portMaxDraft: Number(portMaxDraft),
    estimatedVesselDraft,
  };
};

const isSafeDraftEstimationData = (
  data: Partial<DraftEstimationData>,
): data is DraftEstimationData =>
  data.maxPortDraft !== undefined && data.usedMassCapacity !== undefined;

/**
 * Gets a vessel draft warning for a given event, if any.
 */
export const getDraftWarningAtEvent = memoize(
  (event: TimeData): EventDraftIssue | undefined => {
    const draftEstimationData = event
      ? getDraftEstimationDataFromTimeData(event)
      : UNDEFINED_DRAFT_ESTIMATION_DATA;
    const estimatedDraft =
      event?.event?.vessel && isSafeDraftEstimationData(draftEstimationData)
        ? estimateActualVesselDraft(event.event.vessel)(draftEstimationData)
        : undefined;
    if (draftEstimationData.maxPortDraft && estimatedDraft)
      return getPortDraftWarningInfo(
        estimatedDraft,
        draftEstimationData.maxPortDraft,
      );
    return undefined;
  },
);

/**
 * Calculates estimated draft of a vessel, given its used mass capacity.
 * Uses vessel draft datapoints to perform the estimation, if no datapoints
 * are available returns `vessel.draft` as a fallback value. Beyond the datapoint
 * mass range the draft continues the adjacent end segment's slope (extrapolated,
 * not held flat), so an over- or under-laden draft reads truthfully instead of
 * under-reporting past the deepest datapoint.
 *
 * @param vessel
 * @returns estimated vessel draft or null
 */
export const estimateActualVesselDraft = memoize(
  (vessel: ScenarioTimeline_ShipFragment) => {
    const draftValueFallback = vessel.draft ? Number(vessel.draft) : undefined;
    const draftDatapoints = getVesselDraftDatapoints(vessel);
    return memoize(({ usedMassCapacity }: { usedMassCapacity: number }) => {
      const datapointSegment = getDraftDatapointSegmentFromLoad({
        draftDatapoints,
        cargoLoad: usedMassCapacity,
      });
      if (!datapointSegment) return draftValueFallback;
      try {
        return simpleLerp(usedMassCapacity, datapointSegment);
      } catch (_) {
        return draftValueFallback;
      }
    });
  },
);

export const getVesselDraftFromLoadedMass = memoizeN(
  (
    vessel: ScenarioTimeline_ShipFragment,
    usedMassCapacity: number,
  ): number | undefined =>
    estimateActualVesselDraft(vessel)({ usedMassCapacity }),
);

/**
 * A draft violation as displayed: the port it occurs at, and which side of the call it occurs on when
 * that is known. The shipping plan draws a single call and leaves the side out.
 */
export type DisplayedDraftWarning = EventDraftIssue & {
  portName: string;
  portCallEventType?: PortCallEventType.Arrival | PortCallEventType.Departure;
};

export type SuggestionDraftWarning = DisplayedDraftWarning & {
  /** The port call the vessel is over-draft at. */
  portCallId: TimeData['event']['portCallId'];
  /** Whether the vessel is over-draft arriving at that call or leaving it. */
  portCallEventType: PortCallEventType.Arrival | PortCallEventType.Departure;
  /**
   * The port call the indicator hangs on — the box's own call at or before the violating one. A box
   * shows only its own load and discharge ports, so a violation it causes further along the route
   * has no label of its own to sit on; the load port carries it, which is also where the amount is
   * reduced to respect the limit.
   */
  anchorPortCallId: TimeData['event']['portCallId'];
};

/**
 * The error entry for a port the vessel cannot enter at any load — its empty draft already exceeds the
 * port max. `collectDraftWarnings` surfaces it where the load-dependent warning path stays silent: a
 * ballast-crossed invalid-routing port carries no cargo, so no estimated overdraft is computed for it,
 * yet the route is still impossible. Returns undefined for any satisfiable port.
 */
const invalidRoutingDraftIssue = (
  td: TimeData,
): EventDraftIssue | undefined => {
  const vessel = td.event.vessel;
  const location = 'location' in td.event ? td.event.location : undefined;
  const portMaxDraft = stringToNumOpt(location?.draft);
  if (!vessel || !isDefined(portMaxDraft)) return undefined;
  if (getPortDraftMassCap(vessel, portMaxDraft) !== 0) return undefined;
  return {
    draftIssueSeverity: 'error',
    estimatedVesselDraft: getMinimumVesselDraft(vessel) ?? portMaxDraft,
    portMaxDraft,
  };
};

/** Signed mass of the box's own cargo aboard at `index` — loads add, discharges subtract. */
const ownMassAboardAt = (
  timeData: ReadonlyArray<TimeData>,
  ownCargoIndices: ReadonlyArray<number>,
  index: number,
): number =>
  ownCargoIndices.reduce(
    (sum, i) => (i <= index ? sum + (timeData[i]?.mass ?? 0) : sum),
    0,
  );

/**
 * Collects the strongest draft warning per affected port call, over the span the box's cargo is aboard
 * — its own calls always, and a call it merely passes when its own cargo causes the violation there.
 * Each warning names the port it occurs at and the box's own port call that displays it.
 */
export function collectDraftWarnings(
  timeData: ReadonlyArray<TimeData>,
  assignmentIndices: ReadonlyArray<{
    loadIndex: number;
    dischargeIndex: number;
  }>,
): SuggestionDraftWarning[] {
  if (assignmentIndices.length === 0) return [];

  const ownCargoIndices = assignmentIndices
    .flatMap(({ loadIndex, dischargeIndex }) => [loadIndex, dischargeIndex])
    .sort((a, b) => a - b);
  const ownPortCallIds = new Set(
    ownCargoIndices.map((i) => timeData[i]?.event.portCallId),
  );

  // Keyed on the call and whether it is the arrival or the departure: a port the vessel is too deep
  // both entering and leaving states each separately, as the per-port marker always has.
  const warningsByEvent = new Map<string, SuggestionDraftWarning>();

  for (let index = 0; index < timeData.length; index++) {
    const td = timeData[index];
    if (!td || !isPortCallEventTimeData(td)) continue;
    // Berthing is redundant with arrival
    if (isPortCallBerthingEventTimeData(td)) continue;

    const invalidRouting = invalidRoutingDraftIssue(td);
    const draftIssue = getDraftWarningAtEvent(td) ?? invalidRouting;
    if (!draftIssue) continue;

    // A port too shallow for the empty vessel is a property of the route, not of what is aboard, so
    // only the boxes calling there answer for it. Anywhere else the load is the cause, and every box
    // whose cargo is aboard is part of that cause.
    const isOwnPortCall = ownPortCallIds.has(td.event.portCallId);
    const carriesOwnCargoHere =
      !invalidRouting && ownMassAboardAt(timeData, ownCargoIndices, index) > 0;
    if (!isOwnPortCall && !carriesOwnCargoHere) continue;

    // A call the box makes displays its own violation. Only a violation further along the route needs
    // redirecting, and it goes to the last call the box makes before it — the arrival that reads
    // deepest precedes the cargo work there, so scanning by index alone would land a port early.
    const precedingOwnCargoIndex =
      findLast(ownCargoIndices, (i) => i <= index) ?? ownCargoIndices[0];
    const anchorPortCallId = isOwnPortCall
      ? td.event.portCallId
      : (timeData[precedingOwnCargoIndex]?.event.portCallId ?? null);

    const portCallEventType = isPortCallArrivalEvent(td.event)
      ? PortCallEventType.Arrival
      : PortCallEventType.Departure;

    warningsByEvent.set(`${td.event.portCallId}-${portCallEventType}`, {
      ...draftIssue,
      portName: td.event.location?.name ?? 'Unknown',
      portCallId: td.event.portCallId,
      portCallEventType,
      anchorPortCallId,
    });
  }

  return [...warningsByEvent.values()];
}

/**
 * The draft warnings an assigned voyage box shows for the orders it renders, read from the voyage's
 * own events so a violation the box causes at a port call it only transits is still found.
 */
export function collectVoyageDraftWarnings(
  voyageEvents: ReadonlyArray<TimeData>,
  orderIds: ReadonlySet<number>,
): SuggestionDraftWarning[] {
  const spanByOrderId = new Map<
    number,
    { loadIndex?: number; dischargeIndex?: number }
  >();

  voyageEvents.forEach((td, index) => {
    if (!isCargoEventTimeData(td) || !orderIds.has(td.event.order.id)) return;
    const span = spanByOrderId.get(td.event.order.id) ?? {};
    if (isLoadEvent(td.event)) span.loadIndex = index;
    else if (isDischargeEvent(td.event)) span.dischargeIndex = index;
    spanByOrderId.set(td.event.order.id, span);
  });

  const assignmentIndices = [...spanByOrderId.values()].flatMap(
    ({ loadIndex, dischargeIndex }) =>
      loadIndex !== undefined && dischargeIndex !== undefined
        ? [{ loadIndex, dischargeIndex }]
        : [],
  );

  return collectDraftWarnings(voyageEvents, assignmentIndices);
}
